import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, ButtonGroup, Input, InputGroup, Spinner, UncontrolledTooltip } from "reactstrap";

import { autoSizeColumns, filterGrid, getFilteredRowCount, onBtnExport } from "../utils/gridUtils";
import { useVsiAppContext } from "../stores/VsiApp";
import themeStyles, { vsiGreen, vsiRed } from "../utils/themeStyles";
import { DeleteAuth, PostAuth, PutAuth } from "../utils/Verbs";
import { VsiMedText, Fade } from "../styled/App.styled";
import DblClickText from "./DblClickText";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";
import { useDebounce } from "../utils/hooks";
import { useSearchParams } from "react-router-dom";
import VsiLabeledSelect from "./VsiLabeledSelect";
import { kebabCaseToTitleCase } from "../utils/Helpers";
import { components } from "react-select";

const CustomColumnConfigIconPrepend = props => {
  return (
    <components.ValueContainer {...props}>
      <div className={"d-flex flex-row align-items-center"}>
        <i className={"fa-light fa-table-tree me-1"} />
        {props.children}
      </div>
    </components.ValueContainer>
  );
};

const SearchControl = withErrorBoundary(
  function(props) {
    const { theme } = useVsiAppContext();
    const { search, onSearchChange, clearSearch, gridText } = props;

    return (
      <div className={"d-flex flex-column"}>
        <InputGroup style={{ minWidth: "100%" }}>
          <Input value={search}
                 onChange={onSearchChange}
                 style={{
                   backgroundColor: themeStyles[theme].altBgColor,
                   color: themeStyles[theme].textColor
                 }}
                 placeholder={"Search and Filter Data"} />
          <Button onClick={clearSearch} style={{ zIndex: 0 }}>
            <i className="fa-solid fa-xmark" />
          </Button>
        </InputGroup>
        <div className={"d-flex align-items-end justify-content-start mt-1"}>
          <DblClickText>
            {gridText}
          </DblClickText>
        </div>
      </div>
    );
  }, GeneralErrorBoundary);

const ColumnControls = withErrorBoundary(
  function(props) {
    const {
      customToolbarButton,
      onRefresh_click,
      isLoading,
      isRefreshing,
      saveConfig,
      setDefaultSettings,
      setColumnsOptions,
      columnsOptions,
      saveParams,
      setSelectedColumn,
      selectedColumn,
      eraseSavedConfig,
      resize,
      onExpandClick,
      onCollapseClick,
      exportCsv,
      gridRef,
      toggleColumnBar,
      isColumnBarOpen,
      gridId,
      noPin
    } = props;

    const { client, user, user: { userColumnsMap }, setUser, getAccessTokenSilently } = useVsiAppContext();
    const [searchParams] = useSearchParams();
    const [isExpanded, setIsExpanded] = useState(false);

    const [inputValue, setInputValue] = useState("");
    const selectRef = useRef(null);

    const handleInputChange = (newValue) => {
      setInputValue(newValue);
    };

    const handleCreate = (inputValue) => {
      const newOption = { value: null, label: inputValue.trim() };
      setColumnsOptions((prev) => [newOption, ...prev]);
      setSelectedColumn(newOption);
      setInputValue("");
    };

    const handleAddNewOption = () => {
      // Clear the input value and focus the input field
      const { pathname } = window.location;
      const pathSplit = pathname.split("/");

      let optNum = userColumnsMap?.[pathname]?.length + 1 || 1;
      let addtlStr = "";
      if (saveParams) {
        const paramsKeyed = Object.keys(saveParams);
        paramsKeyed.forEach((key, i) => {
          if (i === 0) {
            addtlStr += " - ";
          }
          addtlStr += kebabCaseToTitleCase(saveParams[key]);

          if (i > 0 && i < paramsKeyed.length - 1) {
            addtlStr += "/";
          }
        });
      }
      let defaultName = kebabCaseToTitleCase(pathSplit[pathSplit.length - 1]) + addtlStr + " " + optNum;

      while (columnsOptions.some(opt => opt.label === defaultName)) {
        optNum++;
        const nameSplit = defaultName.split(" ");
        delete nameSplit[nameSplit.length - 1];
        defaultName = nameSplit.join(" ") + " " + optNum;
      }
      setInputValue(defaultName);
      setSelectedColumn(null);
      if (selectRef.current) {
        selectRef.current.focus();
        // Use a timeout to allow React-Select to render the input before selecting the text
        setTimeout(() => {
          const inputElement = selectRef.current.inputRef;
          if (inputElement) {
            inputElement.select(); // Highlight the default text
          }
        }, 0);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter" && inputValue.trim()) {
        handleCreate(inputValue); // Create the new option when Enter is pressed
      }
    };

    const handleBlur = () => {
      if (inputValue.trim()) {
        handleCreate(inputValue); // Create the new option when input loses focus
      }
    };

    const taggingContext = searchParams.get("context");
    let csvName = "bu_san_capacities";
    if (taggingContext !== "storage") {
      csvName = `${taggingContext}_tagging`;
    }

    const onClickingExpandCompress = (e) => {
      const newExpand = !isExpanded;
      setIsExpanded(newExpand);
      if (newExpand) {
        onExpandClick(e);
      } else {
        onCollapseClick(e);
      }
    };

    useEffect(() => {
      // Set available column configs and the selected one
      // On unit swap, reset to the updated configs
      const { pathname } = window.location;
      if (userColumnsMap?.hasOwnProperty?.(pathname)) {
        let matchingPathConfig = userColumnsMap[pathname] ?? [];
        if (saveParams) {
          matchingPathConfig = matchingPathConfig.filter(config => {
            return Object.keys(saveParams).every(key => config?.params?.[key] === saveParams?.[key]);
          });

        }
        const allTitles = matchingPathConfig.map(({ name }) => name).filter(name => Boolean(name));

        let defaultColumn;
        const optionsConfig = matchingPathConfig.map(config => {
          let name = config.name;
          let nameIdx = 0;
          if (!name) {
            const pathSplit = config.path.split("/");
            const numberlessName = `${kebabCaseToTitleCase(pathSplit[pathSplit.length - 1])}`;
            name = numberlessName;
            while (allTitles.includes(name)) {
              nameIdx++;
              name = `${numberlessName} ${nameIdx}`;
            }
          }
          if(config.defaultConfig) {
            defaultColumn = {
              label: name,
              value: config._id,
              default: config.defaultConfig
            }
          }
          return {
            label: name,
            value: config._id,
            default: config.defaultConfig
          };
        });

        setColumnsOptions(optionsConfig);
        setSelectedColumn(defaultColumn || (optionsConfig?.[optionsConfig.length - 1] ?? null));
      }
    }, [searchParams]);

    const onSelectChange = async e => {
      if (e) {
        // created a new one
        if (e["__isNew__"]) {
          handleCreate(e);
        } else {
          try {
            const token = await getAccessTokenSilently();
            const newColumnOption = columnsOptions.filter(opt => opt.value === e.value);
            const updatedConfig = await PutAuth(`/api/v6/admin/user_col_config/defaults/${user._id}/${newColumnOption?.[0]?.value}`, {}, token);
            let updatedConfigData = updatedConfig.data.data_updated;
            setUser(user => {
              return {
                ...user,
                userColConfig: user.userColConfig.map(fig => {
                  let newFig = { ...fig };
                  if (updatedConfigData._id === fig._id) {
                    newFig = {
                      ...fig,
                      ...updatedConfigData
                    };
                  } else {
                    newFig = {
                      ...fig,
                      defaultConfig: false
                    }
                  }
                  return newFig;
                })
              };
            });
          } catch (error) {
            console.error(error);
          }
        }
        setSelectedColumn(e);
      } else {
        // deleted (click the x)
        try {
          if (selectedColumn.value) {
            await eraseSavedConfig(selectedColumn);
          }
          // if there's an id saved, use that otherwise use the local label
          const newColumnOptions = columnsOptions.filter(opt => opt.value ? opt.value !== selectedColumn.value : opt.label !== selectedColumn.label);
          setColumnsOptions(newColumnOptions);
          setSelectedColumn(newColumnOptions?.[newColumnOptions.length - 1] ?? null);
        } catch (error) {
          console.error(error);
        }
      }
    };

    return (
      <div className={"d-flex flex-row align-items-center justify-content-center"}>
        <ButtonGroup>
          {noPin ? null : (
            <>
              <Button onClick={handleAddNewOption}
                      color={"secondary"}
                      id={`grid-plus-button-${gridId}`}>
                <i className={`fa-solid fa-plus`} />
              </Button>
              <UncontrolledTooltip target={`grid-plus-button-${gridId}`}>
                Add new column setting
              </UncontrolledTooltip>
              <Button onClick={() => saveConfig(selectedColumn)}
                      disabled={!Boolean(selectedColumn)}
                      color={"success"}
                      id={`grid-pin-button-${gridId}`}>
                <i className={`fa-solid fa-floppy-disk`} />
              </Button>
              <UncontrolledTooltip target={`grid-pin-button-${gridId}`}>
                Save column settings under current name
              </UncontrolledTooltip>
              <VsiLabeledSelect isClearable
                                selectRef={selectRef}
                                id={`grid-config-select-${gridId}`}
                                isCreatable
                                minWidth={280}
                                placeholder={"Type and name new column config..."}
                                onCreateOption={handleCreate}
                                inputValue={inputValue}
                                components={{ ValueContainer: CustomColumnConfigIconPrepend }}
                                onInputChange={handleInputChange}
                                onChange={onSelectChange}
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                defaultValue={selectedColumn}
                                options={columnsOptions} />
              <UncontrolledTooltip target={`grid-config-select-${gridId}`}
                                   delay={{ show: 1000, hide: 100 }}>
                Type to create and name a new configuration. Alternatively, select a previously saved configuration.
              </UncontrolledTooltip>
              <Button onClick={setDefaultSettings}
                      disabled={!Boolean(selectedColumn)}
                      id={`grid-erase-button-${gridId}`}>
                <i className={`fa-solid fa-eraser`} />
              </Button>
              <UncontrolledTooltip target={`grid-erase-button-${gridId}`}>
                Set current settings back to default. This button will not overwrite saves.
              </UncontrolledTooltip>
            </>
          )}
          {onRefresh_click ? (
            <>
              <Button onClick={onRefresh_click} disabled={isLoading} id={`grid-refresh-button-${gridId}`}>
                {isRefreshing ? <Spinner size={"sm"} color={"white"} /> :
                  <i className="fa fa-refresh" aria-hidden="true" />}
              </Button>
              <UncontrolledTooltip target={`grid-refresh-button-${gridId}`}>
                Refresh
              </UncontrolledTooltip>
            </>
          ) : null}
          {customToolbarButton}
          <Button onClick={resize} id={`grid-resize-button-${gridId}`}>
            <i className="fa-solid fa-arrows-left-right" />
          </Button>
          <UncontrolledTooltip target={`grid-resize-button-${gridId}`}>
            Resize columns to fit viewport
          </UncontrolledTooltip>
          <Button onClick={onClickingExpandCompress} id={`grid-expand-button-${gridId}`}>
            <i className={`fa-regular fa-${isExpanded ? "compress" : "expand"}`}
               style={{ color: isExpanded ? "darkred" : vsiGreen }} />
          </Button>
          <UncontrolledTooltip target={`grid-expand-button-${gridId}`}>
            {isExpanded ? "Collapse all grouped rows" : "Expand all grouped rows"}
          </UncontrolledTooltip>
          {exportCsv ? (
            <>
              <Button onClick={() => onBtnExport(gridRef, `${csvName}_${client.clientId}.csv`)}
                      id={`grid-export-csv-button-${gridId}`}>
                <i className="fa-solid fa-file-csv" />
              </Button>
              <UncontrolledTooltip target={`grid-export-csv-button-${gridId}`}>
                Export to CSV
              </UncontrolledTooltip>
            </>
          ) : null}
          <Button onClick={toggleColumnBar}>
            <i className="fa-solid fa-wrench" />
            <i className={`fa-solid fa-caret-${isColumnBarOpen ? "up" : "down"} ps-1`} style={{ fontSize: ".8rem" }} />
          </Button>
        </ButtonGroup>
      </div>
    );
  },
  GeneralErrorBoundary);

function GridToolbar(props) {
  const {
    isRefreshing,
    isLoading,
    saveParams,
    savedColDefs,
    setColDefs,
    setRowCountDisplayed,
    gridRef,
    resize,
    hideColumnControls,
    gridId,
    searchDebounce,
    noPin,
    selectedColumn,
    setSelectedColumn
  } = props;
  const { theme, isTabletScreen, user, setUser, getAccessTokenSilently } = useVsiAppContext();
  const [search, setSearch] = useState("");
  const [colStateSaved, setStateSaved] = useState(false);
  const [colStateDeleted, setStateDeleted] = useState(false);
  const [columnsOptions, setColumnsOptions] = useState([]);
  const hasUnsavedChanges = useMemo(() => columnsOptions.some(opt => !opt.value), [columnsOptions]);
  const debounceSearch = useDebounce(search, searchDebounce);

  useEffect(() => {
    setTimeout(() => {
      setStateDeleted(false);
      setStateSaved(false);
    }, 5000);
  }, [colStateDeleted, colStateSaved]);

  useEffect(() => {
    if (searchDebounce && debounceSearch) {
      filterGrid(gridRef, search);
    } else if (!searchDebounce) {
      filterGrid(gridRef, search);
    }
    setRowCountDisplayed(getFilteredRowCount(gridRef));
  }, [searchDebounce, debounceSearch, search]);

  const saveConfig = async (config) => {
    const currentState = {
      colState: gridRef?.current?.api?.getColumnState(),
      groupState: gridRef?.current?.api?.getColumnGroupState(),
      filterState: gridRef?.current?.api?.getFilterModel()
    };

    let updatedColumnState = currentState.colState.filter(col => col.colId !== "ag-Grid-AutoColumn");
    updatedColumnState = updatedColumnState?.map(col => col.aggFunc?.toString().includes("params") ? {
      ...col,
      aggFunc: col.aggFunc?.toString()
    } : col);

    let colConfig = {
      name: config.label,
      user: user._id,
      path: window.location.pathname,
      colState: updatedColumnState,
      groupState: currentState.groupState,
      filterState: currentState.filterState
    };

    let matchingConfig = user.userColConfig.filter(fig => fig.path === window.location.pathname && (config.label === fig.name));

    if (saveParams) {
      colConfig.params = saveParams;
      matchingConfig = matchingConfig.filter(fig => {
        return Object.keys(saveParams).every(key => fig?.params?.[key] === saveParams[key]);
      });
    }

    const token = await getAccessTokenSilently();
    if (matchingConfig.length > 0) {
      try {
        const configToSave = await PutAuth(`/api/v6/admin/user_col_config/${user._id}/${matchingConfig[0]._id}`, colConfig, token);
        setUser(user => {
          return {
            ...user,
            userColConfig: user.userColConfig.map(fig => {
              let newFig = { ...fig };
              if (config.value === fig._id) {
                newFig = {
                  ...fig,
                  ...configToSave.data.data_updated
                };
              }
              return newFig;
            })
          };
        });

        setStateDeleted(false);
        setStateSaved(true);
      } catch (error) {
        console.error(error);
      }
    } else {
      try {
        const configToSave = await PostAuth("/api/v6/admin/user_col_config", colConfig, token);

        let newConfig = [...user.userColConfig, configToSave.data.data];
        setUser(user => {
          return {
            ...user,
            userColConfig: newConfig,
          }
        });

        const newSelected = { ...selectedColumn };
        setColumnsOptions(prev => {
          return prev.map(option => {
            const newOpt = { ...option };
            if (option.label === configToSave.data.data.name) {
              const id = configToSave.data.data._id;
              newOpt.value = id;
              newSelected.value = id;
            }
            return newOpt;
          });
        });
        if (newSelected.value) {
          setSelectedColumn(newSelected);
        }
        setStateDeleted(false);
        setStateSaved(true);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const eraseSavedConfig = async (config) => {
    let matchingConfig = user.userColConfig.find(fig => fig.path === window.location.pathname && (config.value === fig._id));

    if (matchingConfig) {
      try {
        const token = await getAccessTokenSilently();
        await DeleteAuth(`/api/v6/admin/user_col_config/${matchingConfig.user}/${config.value}`, token);
        let newConfig = [];
        if (saveParams) {
          newConfig = user.userColConfig.filter(fig => {
            if (fig.path === window.location.pathname && config.label === fig.name) {
              return !Object.keys(saveParams).every(key => fig?.params?.[key] === saveParams[key]);
            } else {
              return true;
            }
          });
        } else {
          newConfig = user.userColConfig.filter(fig => config.value ? fig._id !== config.value : (config.label !== fig.name && fig.path !== window.location.pathname));
        }

        setUser(user => {
          return {
            ...user,
              userColConfig: newConfig,
          }
        });

        gridRef?.current?.api?.resetColumnState();
        gridRef?.current?.api.setRowGroupColumns([]);
        gridRef?.current?.api?.setFilterModel(null);
        autoSizeColumns(gridRef);
        setStateSaved(false);
        setStateDeleted(true);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const onSearchChange = event => {
    const filter = event.target.value;
    setSearch(filter);
    // filterGrid(gridRef, filter);
    // setRowCountDisplayed(getFilteredRowCount(gridRef));
  };

  const clearSearch = () => {
    setSearch("");
    filterGrid(gridRef, "");
  };

  const onExpandClick = e => {
    e.preventDefault();
    gridRef.current.api.expandAll();
  };

  const onCollapseClick = e => {
    e.preventDefault();
    gridRef.current.api.collapseAll();
  };

  return (
    <div
      className={`
      d-flex flex-${isTabletScreen ? "column-reverse" : "row"} 
      justify-content-${isTabletScreen ? "center" : "between"} 
      align-items-start bg-${theme} p-2 flex-wrap`
    }>
      <SearchControl {...props}
                     onSearchChange={onSearchChange}
                     clearSearch={clearSearch}
                     search={search} />
      {colStateSaved ?
        (<Fade className={"d-flex align-items-end justify-content-start"}>
          <VsiMedText className={`text-end pe-3`}>Grid configurations saved!</VsiMedText>
        </Fade>) : null}
      {colStateDeleted ?
        (<Fade className={"d-flex align-items-end justify-content-start"}>
          <VsiMedText className={`text-end pe-3`}>Grid configurations reset!</VsiMedText>
        </Fade>) : null}
      {hasUnsavedChanges ? (
        <Fade className={"d-flex align-items-end justify-content-end"}>
          <VsiMedText style={{ color: vsiRed }} className={`text-end pe-3`}>* You have an unsaved new
            column.</VsiMedText>
        </Fade>
      ) : null}
      {!hideColumnControls ?
        <ColumnControls {...props}
                        isLoading={isLoading}
                        gridId={gridId}
                        noPin={noPin}
                        setColDefs={setColDefs}
                        setSelectedColumn={setSelectedColumn}
                        selectedColumn={selectedColumn}
                        savedColDefs={savedColDefs}
                        saveParams={saveParams}
                        isRefreshing={isRefreshing}
                        saveConfig={saveConfig}
                        columnsOptions={columnsOptions}
                        setColumnsOptions={setColumnsOptions}
                        eraseSavedConfig={eraseSavedConfig}
                        resize={resize}
                        onCollapseClick={onCollapseClick}
                        onExpandClick={onExpandClick}
        />
        : null}
    </div>
  );
}

export default withErrorBoundary(GridToolbar, GeneralErrorBoundary);
